import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Button, Col, Icons, Row,
} from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getColorByType, getResponseError, setFormError } from '../../../utils/utils';
import { createZone, editZoneById } from '../actions';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import ZoneDTO from '../../../dto/ZoneDTO';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import {
  SEASON_TYPE_LIST, WEEKDAYS_LIST, ZONE_STATUS_LIST, ZONE_TYPE, ZONE_TYPE_LIST,
} from '../../../configs';
import TimePicker from '../../../components/fields/TimePicker/TimePicker';
import DateRangeDTO from '../../../dto/DateRangeDTO';
import ScheduleDTO from '../../../dto/ScheduleDTO';
import Switches from '../../../components/fields/Switches/Switches';
import MultipleDatePicker from '../../../components/fields/DatePicker/MultipleDatePicker';
import Empty from '../../../components/base/Empty';

export default function ZoneForm(props) {
  const {
    handleClose = () => {},
    loadData = () => {},
    zone = null,
    polygon = null,
    mapInstance = null,
  } = props;
  const dispatch = useDispatch();
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoneType, setZoneType] = useState(null);
  const {
    reset, control, getValues, setError, setValue, watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: zone || { status: 1 },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'schedule.time_ranges' });

  useEffect(() => {
    if (polygon) {
      const map = polygon.getMap();
      const stateMonitor = new window.ymaps.Monitor(polygon.editor.state);
      stateMonitor.add('drawing', newValue => setIsDrawing(newValue));
      if (map) {
        polygon.editor.startDrawing();
      }
      polygon.events.add('mapchange', event => {
        const map = polygon.getMap();
        if (map) {
          polygon.editor.startDrawing();
        }
      });
      polygon.geometry.events.add('change', event => {
        const coordinates = polygon.geometry.getCoordinates();
        setValue('area', { type: 'Polygon', coordinates });
      });
      return () => {
        polygon.events.remove('mapchange');
        polygon.geometry.events.remove('change');
        polygon.editor.stopDrawing();
      };
    }
  }, [polygon, mapInstance]);

  useEffect(() => {
    const subscription = watch(values => {
      if (values && typeof values.type !== 'undefined') {
        polygon.options.set('fillColor', getColorByType(values.type));
        setZoneType(values.type);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    reset({});
    setTimeout(() => reset(zone ? new ZoneDTO(zone).getFormData() : {}), 100);
  }, [JSON.stringify(zone)]);

  const handleDrawing = () => {
    const map = polygon.getMap();
    if (map) {
      if (!polygon.editor.state.get('drawing')) {
        polygon.editor.startDrawing();
        return;
      }
      polygon.editor.stopDrawing();
    }
  };

  const handleSave = () => {
    const values = new ZoneDTO(getValues()).getFormData();
    const action = zone ? editZoneById({ ...values, id: zone.id }) : createZone(values);
    dispatch(action)
      .then(() => {
        successNotification('Сохранение зоны', 'Зона сохранена');
        loadData();
        handleClose();
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение зоны', getResponseError(response));
      });
  };

  const dto = new ZoneDTO();
  const attributes = dto.getAttributesByName();
  const dateRangeDto = new DateRangeDTO();
  const dateRangeAttributes = dateRangeDto.getAttributesByName();
  const scheduleDto = new ScheduleDTO();
  const scheduleAttributes = scheduleDto.getAttributesByName();

  return (
    <div className="list-columns card py-3 zone-form">
      <div className="list-header px-3 mb-3">
        <div className="fs-5 fw-semibold ">{zone ? 'Редактирование зоны' : 'Новая зона'}</div>
        <Button className="small-button ms-auto" variant="second" onClick={handleClose}>
          <Icons customIcon icon="close" />
        </Button>
      </div>
      <div className="list-content-wrapper">
        <div className="list-content-body px-3">
          <div />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={TextInputWithLabel}
            dtoObject={attributes.name}
            variant="material"
            className="mb-3 mt-2"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={TextInputWithLabel}
            dtoObject={attributes.description}
            variant="material"
            textarea
            className="mb-3"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={attributes.status}
            variant="material"
            withLabel
            options={ZONE_STATUS_LIST}
            className="mb-3"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={attributes.type}
            variant="material"
            withLabel
            options={ZONE_TYPE_LIST}
            className="mb-3"
          />
          {
            [ZONE_TYPE.PERMITTED, ZONE_TYPE.CONDITIONAL].includes(zoneType) ? (
              <FieldDTOWithCol
                control={control}
                type="text"
                component={TextInputWithLabel}
                dtoObject={attributes.capacity}
                variant="material"
                className="mb-3"
              />
            ) : null
          }
          {
            [ZONE_TYPE.CONDITIONAL].includes(zoneType) ? (
              <>
                {fields.map((field, index) => (
                  <Row key={field.id}>
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TimePicker}
                      dtoObject={dateRangeAttributes.start}
                      variant="material"
                      withLabel
                      className="mb-3"
                      namePrefix={`schedule.time_ranges.${index}.`}
                      columns={5}
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TimePicker}
                      dtoObject={dateRangeAttributes.end}
                      variant="material"
                      withLabel
                      className="mb-3"
                      namePrefix={`schedule.time_ranges.${index}.`}
                      columns={5}
                    />
                    <Col columns={1}>
                      <Button
                        variant="secondary"
                        className="delete-range"
                        onClick={() => remove(index)}
                      >
                        <Icons icon="trash" />
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  variant="secondary"
                  className="mb-3"
                  onClick={() => append({ start: '', end: '' })}
                >
                  Добавить временной промежуток
                </Button>
                <FieldDTOWithCol
                  control={control}
                  type="text"
                  component={CustomSelectInput}
                  dtoObject={scheduleAttributes.weekdays}
                  variant="material"
                  withLabel
                  options={WEEKDAYS_LIST}
                  multiple
                  className="mb-3"
                  namePrefix="schedule."
                />
                <FieldDTOWithCol
                  control={control}
                  type="text"
                  component={Switches}
                  dtoObject={scheduleAttributes.exclude_holidays}
                  variant="material"
                  withLabel
                  className="mb-3"
                  namePrefix="schedule."
                />
                <FieldDTOWithCol
                  control={control}
                  type="text"
                  component={MultipleDatePicker}
                  dtoObject={scheduleAttributes.special_dates}
                  variant="material"
                  withLabel
                  className="mb-3"
                  namePrefix="schedule."
                  changeFormat="YYYY-MM-DD"
                />
              </>
            ) : null
          }
          <Row>
            <Col columns={12} className="text-end">
              {!zone && !isDrawing ? (
                <Button className="small-button" variant="second" onClick={handleDrawing}>
                  <Icons icon="geo-alt" />
                </Button>
              ) : null}
            </Col>
          </Row>

        </div>
      </div>
      <div className="list-footer px-3 d-flex justify-content-around">
        <Button className="flex-sm-fill me-2" onClick={() => handleSave()}>{zone ? 'Сохранить' : 'Создать'}</Button>
        <Button className="flex-sm-fill ms-2" variant="secondary" onClick={handleClose}>Отменить</Button>
      </div>
    </div>
  );
}
